"use client"

import type React from "react"

import { useState, useEffect } from "react"
import Card from "components/Card"
import Button from "components/Button"
import Checkbox from "components/Checkbox"
import { getEmployees, getLeaveTypes, postBulkLeaveInfo, getEmployeeLeavesTypes } from "api/apiHelper"
import type { LeaveInfoType } from "features/leaves/RegisterLeaveTypeForm"
import { toast } from "react-toastify"
import type { AssignMaxLeavesType } from "types/leaves-types"
import { BsCheckCircleFill } from "react-icons/bs"
import { createColumnHelper, type ColumnDef } from "@tanstack/react-table"
import TableHeader from "components/TableHeader"
import TableCell from "components/TableCell"
import ErrorBox from "components/ErrorBox"

interface EmployeeType {
  id: string;
  first_name: string;
  last_name: string;
  designation?: string; // Add designation field
}

// Updated to match the new backend structure
interface AssignmentMatrixResponse {
  [employeeId: string]: {
    emp_name: string;
    leave_details: {
      [leaveTypeDescription: string]: {
        max_leaves_allowed: number;
        remaining_leaves: number;
      };
    };
  };
}

// Enhanced interface for table display
interface EmployeeTableData {
  id: string
  employee_name: string
  designation?: string // Add designation field
  selected: boolean
  assignment_info: {
    id?: string
    max_leaves_allowed?: number
    remaining_leaves?: number
    is_assigned: boolean
  }
}

const AssignMaxLeavesForm: React.FC = () => {
  const [employeesData, setEmployeesData] = useState<EmployeeType[]>([])
  const [leaveTypesData, setLeaveTypesData] = useState<LeaveInfoType[]>([])
  const [assignmentMatrix, setAssignmentMatrix] = useState<AssignmentMatrixResponse>({})
  const [selectedLeaveType, setSelectedLeaveType] = useState<string>("")
  const [assignMaxLeavesData, setAssignMaxLeavesData] = useState<AssignMaxLeavesType>({
    employees: [],
    leave_types: [],
    max_leaves_allowed: "",
    period_start: "",
    period_end: "",
  })
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([])
  const [selectAllEmployees, setSelectAllEmployees] = useState(false)
  const [error, setError] = useState<string>("")

  const fetchEmployees = async () => {
    try {
      const response = await getEmployees(true);
      console.log("Employees Response: ", response.data);
      
      const employeesList = Array.isArray(response.data) 
        ? response.data 
        : response.data.results || [];
      
      console.log("Employees List: ", employeesList);
      setEmployeesData(employeesList);
    } catch (error: any) {
      console.error("Error fetching employees:", error);
      setError(error.message);
      setEmployeesData([]);
    }
  }


  const fetchLeaveTypes = async () => {
    try {
      const response = await getLeaveTypes()
      setLeaveTypesData(response.data)
    } catch (error) {
      console.log(error)
      toast.error("Failed to fetch leave types.")
    }
  }

  const fetchEmployeeAssignments = async () => {
    try {
      const response = await getEmployeeLeavesTypes()
      console.log("Employee assignments response:", response.data)
      
      // The response now directly matches our AssignmentMatrixResponse interface
      setAssignmentMatrix(response.data || {})
    } catch (error) {
      console.log("Error fetching employee assignments:", error)
      setAssignmentMatrix({})
      toast.error("Failed to fetch employee assignments.")
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      await fetchEmployees()
      await fetchLeaveTypes()
    }
    fetchData()
  }, [])

  // Fetch assignments after employees are loaded
  useEffect(() => {
    if (employeesData.length > 0) {
      fetchEmployeeAssignments()
    }
  }, [employeesData])

  // Get assignment details for a specific employee and leave type
  const getEmployeeLeaveAssignment = (employeeId: string, leaveTypeId: string) => {
    // Get the employee data from the matrix
    const employeeData = assignmentMatrix[employeeId]
    if (!employeeData || !employeeData.leave_details) return null

    // Get the leave type description from the selected leave type ID
    const selectedLeaveTypeData = leaveTypesData.find((lt) => lt.id.toString() === leaveTypeId)
    if (!selectedLeaveTypeData) return null

    const leaveTypeDescription = selectedLeaveTypeData.description

    // Check if this employee has this leave type assigned
    const assignmentInfo = employeeData.leave_details[leaveTypeDescription]
    
    if (assignmentInfo) {
      return {
        id: `${employeeId}_${leaveTypeDescription}`,
        leave_type: leaveTypeDescription,
        leave_type_name: leaveTypeDescription,
        max_leaves_allowed: assignmentInfo.max_leaves_allowed,
        remaining_leaves: assignmentInfo.remaining_leaves,
      }
    }

    return null
  }

  // Prepare table data when leave type is selected
  const tableData: EmployeeTableData[] = employeesData.map((employee) => {
    const assignment = selectedLeaveType ? getEmployeeLeaveAssignment(employee.id, selectedLeaveType) : null
    return {
      id: employee.id,
      employee_name: `${employee.first_name} ${employee.last_name}`,
      designation: employee.designation,
      selected: selectedEmployeeIds.includes(employee.id),
      assignment_info: {
        id: assignment?.id,
        max_leaves_allowed: assignment?.max_leaves_allowed,
        remaining_leaves: assignment?.remaining_leaves,
        is_assigned: !!assignment,
      },
    }
  }).sort((a, b) => a.employee_name.localeCompare(b.employee_name))

  const handleEmployeeCheckboxChange = (employeeId: string) => {
    setSelectedEmployeeIds((prevSelected) => {
      if (prevSelected.includes(employeeId)) {
        return prevSelected.filter((id) => id !== employeeId)
      } else {
        return [...prevSelected, employeeId]
      }
    })
  }

  const handleSelectAllEmployees = () => {
    if (selectAllEmployees) {
      setSelectedEmployeeIds([])
    } else {
      setSelectedEmployeeIds(employeesData.map((employee) => employee.id))
    }
    setSelectAllEmployees(!selectAllEmployees)
  }

  useEffect(() => {
    if (selectedEmployeeIds.length === employeesData.length && employeesData.length > 0) {
      setSelectAllEmployees(true)
    } else {
      setSelectAllEmployees(false)
    }
  }, [selectedEmployeeIds, employeesData])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { id, value } = e.target
    setAssignMaxLeavesData({
      ...assignMaxLeavesData,
      [id]: value,
    })
  }

  const handleLeaveTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedLeaveType(e.target.value)
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError("");
    if (selectedEmployeeIds.length === 0) {
      toast.warning("Please select at least one employee.")
      return
    }
    if (!selectedLeaveType) {
      toast.warning("Please select a leave type.")
      return
    }
    if (
      !assignMaxLeavesData.max_leaves_allowed ||
      !assignMaxLeavesData.period_start ||
      !assignMaxLeavesData.period_end
    ) {
      toast.warning("Please fill in all the required fields.")
      return
    }

    const notification = toast.loading("Assigning Max Leaves...")
    try {
      const payload = {
        employees: selectedEmployeeIds,
        leave_types: [selectedLeaveType],
        max_leaves_allowed: assignMaxLeavesData.max_leaves_allowed,
        period_start: assignMaxLeavesData.period_start,
        period_end: assignMaxLeavesData.period_end,
      }
      const response = await postBulkLeaveInfo(payload)
      console.log(response)
      toast.update(notification, {
        render: "Leaves assigned successfully",
        type: "success",
        isLoading: false,
        autoClose: 5000,
      })
      // Refresh the assignments after successful submission
      await fetchEmployeeAssignments()
      handleReset()
    } catch (error: any) {
      console.log(error)
      let errorMessage = "Failed to assign Max Leaves"
      if (error.response && error.response.data) {
        if (Array.isArray(error.response.data)) {
          errorMessage = error.response.data.join("\n")
        } else if (typeof error.response.data === "object") {
          errorMessage = Object.values(error.response.data).join("\n")
        } else {
          errorMessage = error.response.data.toString()
        }
      }
      toast.update(notification, {
        render: "Failed to Assign Leaves",
        type: "error",
        isLoading: false,
        autoClose: 5000,
      })
      setError(errorMessage);
    }
  }

  const handleReset = () => {
    setAssignMaxLeavesData({
      employees: [],
      leave_types: [],
      max_leaves_allowed: "",
      period_start: "",
      period_end: "",
    })
    setSelectedEmployeeIds([])
    setSelectAllEmployees(false)
    setSelectedLeaveType("")
  }

  const selectedLeaveTypeName =
    leaveTypesData.find((lt) => lt.id.toString() === selectedLeaveType)?.description || ""

  // Table column definitions
  const columnHelper = createColumnHelper<EmployeeTableData>()
  const columns: ColumnDef<EmployeeTableData, any>[] = [
    // Checkbox column
    columnHelper.display({
      id: "select",
      header: () => (
        <TableHeader>
          <div className="flex justify-center">
            <Checkbox checked={selectAllEmployees} onChange={handleSelectAllEmployees} color="blue" />
          </div>
        </TableHeader>
      ),
      cell: (info) => (
        <TableCell>
          <div className="flex justify-center">
            <Checkbox
              checked={info.row.original.selected}
              onChange={() => handleEmployeeCheckboxChange(info.row.original.id)}
              color="blue"
            />
          </div>
        </TableCell>
      ),
      size: 60,
    }),
    // Employee Name column
    columnHelper.accessor("employee_name", {
      id: "employee_name",
      header: () => <TableHeader>Employee Name</TableHeader>,
      cell: (info) => {
        const employee = info.row.original;
        return (
          <TableCell>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                <i className="fas fa-user text-blue-600 dark:text-blue-400 text-sm"></i>
              </div>
              <div>
                <span className="font-medium text-gray-900 dark:text-white">{info.getValue()}</span>
                {employee.designation && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {employee.designation}
                  </div>
                )}
              </div>
            </div>
          </TableCell>
        );
      },
      size: 200,
    }),
  ]

  // Add leave type column if selected
  if (selectedLeaveType && selectedLeaveTypeName) {
    columns.push(
      columnHelper.display({
        id: "leave_assignment",
        header: () => (
          <TableHeader>
            <div className="text-center">
              <div className="font-semibold flex items-center justify-center gap-2">
                <i className="fas fa-calendar-check text-blue-600 dark:text-blue-400"></i>
                {selectedLeaveTypeName}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Assignment Status</div>
            </div>
          </TableHeader>
        ),
        cell: (info) => {
          const assignmentInfo = info.row.original.assignment_info
          return (
            <TableCell>
              <div className="flex justify-center">
                {assignmentInfo.is_assigned ? (
                  <div className="flex items-center gap-3 px-4 py-2 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                    <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                      <BsCheckCircleFill size={16} className="text-green-600 dark:text-green-400" />
                    </div>
                    <div className="text-left">
                      <div className="text-sm font-semibold text-green-700 dark:text-green-300">Assigned</div>
                      <div className="text-xs text-gray-600 dark:text-gray-400">
                        {assignmentInfo.max_leaves_allowed} days
                        {assignmentInfo.remaining_leaves !== undefined && (
                          <> • {assignmentInfo.remaining_leaves} remaining</>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center space-x-3 px-4 py-2 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                    <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                      <i className="fas fa-exclamation text-red-600 dark:text-red-400 text-sm"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-red-700 dark:text-red-300">
                        Not Assigned
                      </div>
                      <div className="text-xs text-red-600 dark:text-red-400">
                        No allocation yet
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </TableCell>
          )
        },
        size: 250,
      }),
    )
  }

  return (
    <Card extra="w-full h-full p-6">
      {/* Title Section */}
      <div className="mb-8">
        <h4 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Assign Max Leaves</h4>
        <p className="text-gray-600 dark:text-gray-400">Configure leave allowances for your employees</p>
      </div>

      <ErrorBox errorMessage={error} />

      <form className="space-y-8" onSubmit={handleSubmit} onReset={handleReset}>
        {/* Configuration Section */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/10 dark:to-indigo-900/10 rounded-xl p-6 border border-blue-100 dark:border-blue-800/30">
          <div className="flex items-center mb-6">
            <i className="fas fa-cog mr-2 text-blue-600"></i>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Leave Configuration</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Set up leave type and allowance details</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Leave Type Selection */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                <i className="fas fa-calendar-alt mr-2 text-blue-600"></i>
                Leave Type *
              </label>
              <select
                value={selectedLeaveType}
                onChange={handleLeaveTypeChange}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                required
              >
                <option value="">Choose leave type...</option>
                {leaveTypesData.map((leave) => (
                  <option key={leave.id} value={leave.id.toString()}>
                    {leave.description}
                  </option>
                ))}
              </select>
            </div>

            {/* Max Leaves */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                <i className="fas fa-hashtag mr-2 text-green-600"></i>
                Max Leaves *
              </label>
              <input
                id="max_leaves_allowed"
                type="number"
                placeholder="Enter days..."
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                onChange={handleChange}
                value={assignMaxLeavesData.max_leaves_allowed}
                required
              />
            </div>

            {/* Period */}
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                <i className="fas fa-calendar-range mr-2 text-purple-600"></i>
                Period *
              </label>
              <div className="space-y-2">
                <input
                  id="period_start"
                  type="date"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-blue-500"
                  onChange={handleChange}
                  value={assignMaxLeavesData.period_start}
                  required
                />
                <input
                  id="period_end"
                  type="date"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-1 focus:ring-blue-500"
                  onChange={handleChange}
                  value={assignMaxLeavesData.period_end}
                  required
                />
              </div>
            </div>
          </div>
        </div>

        {/* Employee Selection Section */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <i className="fas fa-users mr-2 text-blue-600 dark:text-blue-400"></i>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Employee Selection
                  {selectedLeaveType && selectedLeaveTypeName && (
                    <span className="ml-2 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 text-sm rounded-full">
                      {selectedLeaveTypeName}
                    </span>
                  )}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Select employees to assign leave allowances
                </p>
              </div>
            </div>
            
            {/* Quick Stats */}
            {selectedLeaveType && tableData.length > 0 && (
              <div className="flex items-center space-x-4">
                <div className="text-center">
                  <div className="text-lg font-bold text-blue-600 dark:text-blue-400">{selectedEmployeeIds.length}</div>
                  <div className="text-xs text-gray-500">Selected</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-green-600 dark:text-green-400">
                    {tableData.filter(emp => emp.assignment_info.is_assigned).length}
                  </div>
                  <div className="text-xs text-gray-500">Assigned</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-red-600 dark:text-red-400">
                    {tableData.filter(emp => !emp.assignment_info.is_assigned).length}
                  </div>
                  <div className="text-xs text-gray-500">Unassigned</div>
                </div>
              </div>
            )}
          </div>

          {employeesData.length > 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
              {/* Table Header */}
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <Checkbox 
                      checked={selectAllEmployees} 
                      onChange={handleSelectAllEmployees} 
                      color="blue" 
                    />
                    <span className="font-semibold text-gray-900 dark:text-white">
                      Select All ({tableData.length} employees)
                    </span>
                  </div>
                  {selectedLeaveType && (
                    <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                      <i className="fas fa-info-circle"></i>
                      <span>Assignment status for {selectedLeaveTypeName}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Employee List */}
              <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-96 overflow-y-auto">
                
                {tableData.map((employee, index) => (
                  <div 
                    key={employee.id} 
                    className={`px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                      employee.selected ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      {/* Employee Info */}
                      <div className="flex items-center space-x-4">
                        <Checkbox
                          checked={employee.selected}
                          onChange={() => handleEmployeeCheckboxChange(employee.id)}
                          color="blue"
                        />
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                            <i className="fas fa-user text-blue-600 dark:text-blue-400 text-sm"></i>
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900 dark:text-white">
                              {employee.employee_name}
                            </div>
                            {employee.designation && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {employee.designation}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Assignment Status */}
                      {selectedLeaveType && (
                        <div className="flex items-center">
                          {employee.assignment_info.is_assigned ? (
                            <div className="flex items-center space-x-3 px-4 py-2 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                              <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                                <i className="fas fa-check text-green-600 dark:text-green-400 text-sm"></i>
                              </div>
                              <div>
                                <div className="text-sm font-semibold text-green-700 dark:text-green-300">
                                  Assigned
                                </div>
                                <div className="text-xs text-green-600 dark:text-green-400">
                                  {employee.assignment_info.max_leaves_allowed} days
                                  {employee.assignment_info.remaining_leaves !== undefined && (
                                    <> • {employee.assignment_info.remaining_leaves} remaining</>
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-3 px-4 py-2 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                              <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                                <i className="fas fa-exclamation text-red-600 dark:text-red-400 text-sm"></i>
                              </div>
                              <div>
                                <div className="text-sm font-semibold text-red-700 dark:text-red-300">
                                  Not Assigned
                                </div>
                                <div className="text-xs text-red-600 dark:text-red-400">
                                  No allocation yet
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-16 bg-gray-50 dark:bg-gray-800/50 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600">
              <div className="w-16 h-16 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fas fa-users text-2xl text-gray-400"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No employees found</h3>
              <p className="text-gray-500 dark:text-gray-400">Please ensure employees are created first</p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-600">
          <Button type="reset" varient="secondary" className="px-8 py-3">
            <i className="fas fa-times mr-2"></i>
            Reset
          </Button>
          <Button 
            type="submit" 
            varient="primary" 
            className="px-8 py-3"
            disabled={!selectedLeaveType || selectedEmployeeIds.length === 0}
          >
            <i className="fas fa-paper-plane mr-2"></i>
            Assign Leaves ({selectedEmployeeIds.length})
          </Button>
        </div>
      </form>
    </Card>
  )
}

export default AssignMaxLeavesForm